// =============================================================================
// Auth API Client - Dual-method authentication endpoints
// =============================================================================
// Phase 5: Frontend Implementation
// Supports phone (SMS) and email (OTP) authentication
// =============================================================================

import { api } from './api';
import type { AuthUser, AuthOrganization, IdentifierType } from '../stores/auth';

// =============================================================================
// Request Types
// =============================================================================

export interface SendCodeRequest {
  identifier: string;
  identifierType?: IdentifierType;
}

export interface VerifyCodeRequest {
  identifier: string;
  identifierType?: IdentifierType;
  code: string;
}

export interface SignupRequest {
  signupToken: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  licenseNumber?: string;
  userType?: 'AGENT' | 'TRANSACTION_COORDINATOR';
  termsAccepted: boolean;
}

// =============================================================================
// Response Types
// =============================================================================

export interface SendCodeResponse {
  success: boolean;
  message: string;
  maskedIdentifier?: string;
  codeExpiresAt?: string;
  nextStep?: 'VERIFY_CODE';
  error?: {
    code: string;
    message: string;
    retryAfter?: number;
  };
}

export interface VerifyResponseLogin {
  success: true;
  flow: 'login';
  isAuthenticated: true;
  user: AuthUser;
  organization: AuthOrganization;
  message: string;
}

export interface VerifyResponseSignup {
  success: true;
  flow: 'signup';
  isAuthenticated: false;
  signupToken: string;
  verifiedIdentifier: string;
  verifiedIdentifierType: IdentifierType;
  signupTokenExpiresAt?: string;
  message: string;
  nextStep: 'COMPLETE_SIGNUP';
}

export interface VerifyResponseError {
  success: false;
  error: {
    code: string;
    message: string;
    attemptsRemaining?: number;
    retryAfter?: number;
  };
}

export type VerifyCodeResponse = VerifyResponseLogin | VerifyResponseSignup | VerifyResponseError;

export interface SignupResponse {
  success: true;
  user: AuthUser;
  organization: AuthOrganization;
  message: string;
}

export interface SignupResponseError {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export interface MeResponse {
  success: true;
  user: AuthUser;
  organization: AuthOrganization;
}

export interface MeResponseError {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format phone number for API
 * Converts (555) 123-4567 → +15551234567
 */
export function formatPhoneForApi(phone: string): string {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // If already has country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  // Add US country code
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // Return as-is if already formatted correctly
  if (phone.startsWith('+')) {
    return phone;
  }

  return `+${digits}`;
}

/**
 * Format phone number for display
 * Converts +15551234567 → (555) 123-4567
 */
export function formatPhoneForDisplay(phone: string): string {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Remove country code if present
  const nationalNumber = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

  if (nationalNumber.length === 10) {
    return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
  }

  return phone;
}

/**
 * Detect identifier type from value
 */
export function detectIdentifierType(identifier: string): IdentifierType {
  // Simple email check
  if (identifier.includes('@')) {
    return 'email';
  }
  return 'phone';
}

/**
 * Normalize identifier for API
 */
export function normalizeIdentifier(identifier: string, type: IdentifierType): string {
  if (type === 'email') {
    return identifier.toLowerCase().trim();
  }
  return formatPhoneForApi(identifier);
}

// =============================================================================
// Auth API Functions
// =============================================================================

/**
 * Request a verification code (phone SMS or email OTP)
 */
export async function sendCode(data: SendCodeRequest): Promise<SendCodeResponse> {
  const normalizedData = {
    identifier: normalizeIdentifier(
      data.identifier,
      data.identifierType || detectIdentifierType(data.identifier)
    ),
    identifierType: data.identifierType || detectIdentifierType(data.identifier),
  };
  return api.post<SendCodeResponse>('/auth/send-code', normalizedData);
}

/**
 * Legacy alias for sendCode
 */
export async function login(data: SendCodeRequest): Promise<SendCodeResponse> {
  return sendCode(data);
}

/**
 * Verify the 6-digit code
 */
export async function verifyCode(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
  const normalizedData = {
    identifier: normalizeIdentifier(
      data.identifier,
      data.identifierType || detectIdentifierType(data.identifier)
    ),
    identifierType: data.identifierType || detectIdentifierType(data.identifier),
    code: data.code,
  };
  return api.post<VerifyCodeResponse>('/auth/verify', normalizedData);
}

/**
 * Complete signup after verification
 */
export async function signup(data: SignupRequest): Promise<SignupResponse | SignupResponseError> {
  return api.post<SignupResponse | SignupResponseError>('/auth/signup', data);
}

/**
 * Get current authenticated user
 */
export async function getMe(): Promise<MeResponse | MeResponseError> {
  return api.get<MeResponse | MeResponseError>('/auth/me');
}

/**
 * Log out and destroy session
 */
export async function logout(): Promise<LogoutResponse> {
  return api.post<LogoutResponse>('/auth/logout');
}

/**
 * Accept Terms of Service for current user
 */
export interface AcceptTermsResponse {
  success: boolean;
  termsAcceptedVersion?: string;
  error?: string;
}

export async function acceptTerms(): Promise<AcceptTermsResponse> {
  return api.post<AcceptTermsResponse>('/auth/accept-terms');
}

// =============================================================================
// Profile Update
// =============================================================================

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
  companyName?: string | null;
  licenseNumber?: string | null;
  phone?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  user: AuthUser;
  message: string;
}

export async function updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
  return api.patch<UpdateProfileResponse>('/auth/me', data);
}

// =============================================================================
// Notification Preferences
// =============================================================================

export interface NotificationPrefs {
  packageActivity: boolean;
}

export interface UpdateNotificationPrefsRequest {
  packageActivity?: boolean;
}

export interface UpdateNotificationPrefsResponse {
  success: true;
  notificationPrefs: NotificationPrefs;
}

export async function updateNotificationPrefs(
  data: UpdateNotificationPrefsRequest
): Promise<UpdateNotificationPrefsResponse> {
  return api.patch<UpdateNotificationPrefsResponse>('/auth/me/notification-prefs', data);
}

// =============================================================================
// Share-Link Signup (Gatekeeper Flow)
// =============================================================================

export interface CheckUserResponse {
  exists: boolean;
  firstName?: string;
  // Identifier-first login (2026-09-15): whether the account has a password
  hasPassword?: boolean;
}

export async function checkUser(email: string): Promise<CheckUserResponse> {
  return api.post<CheckUserResponse>('/auth/check-user', { email });
}

export interface ShareSignupRequest {
  signupToken: string;
  firstName: string;
  lastName: string;
  phone?: string;
  userType: 'AGENT' | 'TRANSACTION_COORDINATOR';
  shareId: string;
  termsAccepted: true;
}

export interface ShareSignupResponse {
  success: true;
  user: AuthUser;
  organization: AuthOrganization;
}

export async function shareSignup(data: ShareSignupRequest): Promise<ShareSignupResponse> {
  return api.post<ShareSignupResponse>('/auth/share-signup', data);
}

// =============================================================================
// Password Authentication (v1, 2026-09)
// =============================================================================

export interface PasswordLoginOkResponse {
  success: true;
  status: 'ok';
  isAuthenticated: true;
  user: AuthUser;
  organization: AuthOrganization;
  message: string;
}

export interface PasswordLoginMfaResponse {
  success: true;
  status: 'mfa_required';
  maskedPhone: string;
  codeExpiresAt: string;
  message: string;
}

export type PasswordLoginApiResponse = PasswordLoginOkResponse | PasswordLoginMfaResponse;

export async function loginPassword(data: { email: string; password: string }): Promise<PasswordLoginApiResponse> {
  return api.post<PasswordLoginApiResponse>('/auth/login-password', data);
}

export async function verifyMfa(data: { email: string; code: string }): Promise<PasswordLoginOkResponse> {
  return api.post<PasswordLoginOkResponse>('/auth/verify-mfa', data);
}

export async function forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>('/auth/forgot-password', { email });
}

export async function resetPassword(data: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>('/auth/reset-password', data);
}

export async function changePassword(data: {
  currentPassword?: string;
  newPassword: string;
}): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>('/auth/change-password', data);
}

export async function setMfa(enabled: boolean): Promise<{ success: boolean; mfaEnabled: boolean; message: string }> {
  return api.post<{ success: boolean; mfaEnabled: boolean; message: string }>('/auth/mfa', { enabled });
}

// =============================================================================
// Export API Object
// =============================================================================

export const authApi = {
  sendCode,
  login,
  verifyCode,
  signup,
  getMe,
  logout,
  acceptTerms,
  updateProfile,
  updateNotificationPrefs,
  checkUser,
  shareSignup,
  loginPassword,
  verifyMfa,
  forgotPassword,
  resetPassword,
  changePassword,
  setMfa,
};
